import { NextSeoProps } from "next-seo";
import { z, ZodFormattedError } from "zod";
import { LandingPageEvent } from "~/common/constants";
import { PostSummarySchema } from "~/components/Sidecar/sidecar-schemas";
import { getValueOrDefault } from "~/lib/shared/getValueOrDefault";
import { heDecodeOrDefault } from "~/lib/shared/html-utils";
import { LandingPageStructDataProps } from "~/types/asgard/StructuredData";

const decode = (str: string | unknown): string => heDecodeOrDefault(str, "");

const linkSchema = z.object({
  url: z.string(),
  title: z.string().transform(decode),
});

const imagePropertySchema = z.object({
  url: z.string().url(),
  alt: z.string().nullable().transform(decode),
});

const maxWidthSchema = z.union([
  z.literal("small"),
  z.literal("medium"),
  z.literal("large"),
]);

const ctaAlignmentSchema = z
  .union([z.literal("left"), z.literal("center"), z.literal("right")])
  .nullable()
  .optional()
  .catch("left");

export enum LandingPageV2BlockTypes {
  Anchor = "anchor",
  Downloads = "downloads",
  ContentListCurated = "content_list_curated",
  ContentListTabbed = "tabbed_content_list",
  ContentListSplit = "featured_content_list_split",
  FeatureList = "feature_list",
  Image = "image",
  HubSpotForm = "hubspot_form",
  LeadGenerationForm = "lead_generation_form",
  Search = "search",
  TabbedNavigation = "tabbed_navigation",
  Wysiwyg = "wysiwyg",
  YoutubeEmbed = "youtube_video",
  InvalidProperties = "invalid_properties",
  UnknownBlockType = "invalid_blocktype",
}

const landingPageBlockTypesSchema = z.nativeEnum(LandingPageV2BlockTypes);

const tokensToRemove = /(?:background:\s*)?([^;]+);?/;
const backgroundStrip = (
  value: string | undefined | null
): string | undefined =>
  getValueOrDefault(value?.match(tokensToRemove)?.[1].trim(), "");

const backgroundSchema = z
  .string()
  .nullable()
  .optional()
  .transform(backgroundStrip);

const spacingSchema = z
  .union([
    z.literal("none"),
    z.literal("extraSmall"),
    z.literal("small"),
    z.literal("medium"),
    z.literal("large"),
    z.literal("extraLarge"),
  ])
  .optional();

const baseLandingPageBlockSchema = z.object({
  acfFcLayout: landingPageBlockTypesSchema,
  background: backgroundSchema,
  topSpacing: spacingSchema,
  bottomSpacing: spacingSchema,
});

export type BaseLandingPageBlock = z.infer<typeof baseLandingPageBlockSchema>;

export const AnchorSchema = z.object({
  acfFcLayout: z.literal(LandingPageV2BlockTypes.Anchor),
  anchorId: z.string(),
  appearance: z.string().default("none").optional(),
});

export type AnchorBlock = z.infer<typeof AnchorSchema>;

const fileUploadSchema = z.object({
  id: z.number(),
  title: z.string(),
  filename: z.string(),
  filesize: z.number(),
  url: z.string(),
  alt: z.string().nullable().optional(),
  name: z.string(),
  date: z.string(),
  modified: z.string(),
  mimeType: z.string(),
  type: z.string(),
  subtype: z.string(),
});

const downloadableAssetSchema = z.object({
  title: z.string(),
  permission: z.array(z.string()),
  fileUpload: fileUploadSchema,
  fileDescription: z.string(),
});

export const DownloadableAssetsSchema = baseLandingPageBlockSchema.extend({
  acfFcLayout: z.literal(LandingPageV2BlockTypes.Downloads),
  appearance: z.literal("full_width"),
  maxWidth: maxWidthSchema,
  wysiwyg: z.string().nullable().optional(),
  downloadableAssets: z.array(downloadableAssetSchema),
});

export type DownloadableAssetsBlock = z.infer<typeof DownloadableAssetsSchema>;

export const ContentListCuratedSchema = baseLandingPageBlockSchema.extend({
  acfFcLayout: z.literal(LandingPageV2BlockTypes.ContentListCurated),
  appearance: z.literal("full_width"),
  maxWidth: maxWidthSchema,
  posts: z.array(PostSummarySchema),
  wysiwyg: z.string().nullable().optional(),
});

export type ContentListCuratedBlock = z.infer<typeof ContentListCuratedSchema>;

export const ContentListTabbedSchema = baseLandingPageBlockSchema.extend({
  acfFcLayout: z.literal(LandingPageV2BlockTypes.ContentListTabbed),
  appearance: z.literal("full_width"),
  maxWidth: maxWidthSchema,
  wysiwyg: z.string().nullable().optional(),
  tabs: z.array(
    z.object({
      label: z.string(),
      posts: z.array(PostSummarySchema),
    })
  ),
});

export type ContentListTabbedBlock = z.infer<typeof ContentListTabbedSchema>;

export const ContentListSplitSchema = baseLandingPageBlockSchema.extend({
  acfFcLayout: z.literal(LandingPageV2BlockTypes.ContentListSplit),
  appearance: z.literal("full_width"),
  maxWidth: maxWidthSchema,
  wysiwyg: z.string().nullable().optional(),
  featured: z.object({
    featuredPost: PostSummarySchema,
    sectionTitle: z.string().nullable().optional(),
  }),
  latest: z.object({
    sectionTitle: z.string().nullable().optional(),
    posts: z.array(PostSummarySchema),
  }),
});

export type ContentListSplitBlock = z.infer<typeof ContentListSplitSchema>;

/** Feature List */
const baseFeatureListSchema = baseLandingPageBlockSchema.extend({
  acfFcLayout: z.literal(LandingPageV2BlockTypes.FeatureList),
  maxWidth: maxWidthSchema,
  features: z.array(
    z.object({
      image: imagePropertySchema.nullable().optional(),
      description: z.string(),
    })
  ),
  wysiwyg: z.string().nullable().optional(),
});

export const FeatureListColumnGridSchema = baseFeatureListSchema.extend({
  appearance: z.union([
    z.literal("two_column_grid"),
    z.literal("three_column_grid"),
    z.literal("four_column_grid"),
  ]),
});

export type FeatureListColumnGridBlock = z.infer<
  typeof FeatureListColumnGridSchema
>;

export type FeatureListBlock = z.infer<typeof FeatureListColumnGridSchema>;

export const ImageSchema = baseLandingPageBlockSchema.extend({
  acfFcLayout: z.literal(LandingPageV2BlockTypes.Image),
  appearance: z.literal("full_width"),
  /** Wysiwyg that will display beneath the image */
  caption: z.string().nullable().optional(),
  image: imagePropertySchema,
  maxWidth: maxWidthSchema,
  /** Wysiwyg that will render above the image */
  wysiwyg: z.string().nullable().optional(),
});

export type ImageBlock = z.infer<typeof ImageSchema>;

/** Lead Generation Form */
const baseLeadGenerationSchema = baseLandingPageBlockSchema.extend({
  acfFcLayout: z.literal(LandingPageV2BlockTypes.LeadGenerationForm),
  enableDarkMode: z.boolean().default(false).optional(),
  maxWidth: maxWidthSchema,
  wysiwyg: z.string().nullable().optional(),
  eventName: z.nativeEnum(LandingPageEvent),
  openQuestion: z.string().nullable().transform(decode),
  openQuestionCharacterLimit: z.number().nullable(),
  successMessage: z.string(),
  salesforceCampaignId: z.string(),
  successRedirectUrl: linkSchema,
});

export const LeadGenerationFormFullWidthSchema =
  baseLeadGenerationSchema.extend({
    appearance: z.literal("full_width"),
  });

export type LeadGenerationFormBlock = z.infer<
  typeof LeadGenerationFormFullWidthSchema
>;

export const SearchSchema = baseLandingPageBlockSchema.extend({
  acfFcLayout: z.literal(LandingPageV2BlockTypes.Search),
  appearance: z.literal("full_width"),
  categoryOptions: z.array(z.object({ id: z.number(), name: z.string() })),
  contentTypeOptions: z.array(
    z.object({ value: z.string(), label: z.string() })
  ),
  hideSideFilters: z.boolean().optional().default(false),
  maxWidth: maxWidthSchema,
  topicOptions: z.array(z.object({ id: z.number(), name: z.string() })),
  pageSize: z.string().nullable().optional(),
  sectionTitle: z.string().nullable().optional(),
  wysiwyg: z.string().nullable().optional(),
});

export type SearchBlock = z.infer<typeof SearchSchema>;

export const TabbedNavigationSchema = baseLandingPageBlockSchema.extend({
  acfFcLayout: z.literal(LandingPageV2BlockTypes.TabbedNavigation),
  appearance: z.literal("full_width"),
  maxWidth: maxWidthSchema,
  wysiwyg: z.string().nullable().optional(),
  label: z.string().nullable().optional(),
  links: z.array(
    z.object({
      link: linkSchema,
    })
  ),
});

export type TabbedNavigationBlock = z.infer<typeof TabbedNavigationSchema>;

/** WYSIWYG */
const baseWysiwygSchema = baseLandingPageBlockSchema.extend({
  acfFcLayout: z.literal(LandingPageV2BlockTypes.Wysiwyg),
  maxWidth: maxWidthSchema,
  wysiwyg: z.string(),
  includeCtas: z.boolean().nullable().optional(),
  primaryCta: linkSchema.optional().nullable(),
  secondaryCta: linkSchema.optional().nullable(),
  ctaAlignment: ctaAlignmentSchema,
});

export const WysiwygSplitWithImageSchema = baseWysiwygSchema.extend({
  appearance: z.literal("split_with_image"),
  image: imagePropertySchema,
  imagePosition: z.union([z.literal("left"), z.literal("right")]),
  verticalTextAlignment: z
    .union([z.literal("top"), z.literal("center")])
    .optional()
    .default("top"),
});
export const WysiwygFullWidthSchema = baseWysiwygSchema.extend({
  appearance: z.literal("full_width"),
});
export type WysiwygSplitWithImageBlock = z.infer<
  typeof WysiwygSplitWithImageSchema
>;

export type WysiwygFullWidthBlock = z.infer<typeof WysiwygFullWidthSchema>;

export type WysiwygBlock = z.infer<
  typeof WysiwygFullWidthSchema | typeof WysiwygSplitWithImageSchema
>;

export const YouTubeEmbedSchema = baseLandingPageBlockSchema.extend({
  acfFcLayout: z.literal(LandingPageV2BlockTypes.YoutubeEmbed),
  appearance: z.literal("full_width"),
  maxWidth: maxWidthSchema,
  wysiwyg: z.string().nullable().optional(),
  videoUrl: z.string(),
});

export type YouTubeEmbedBlock = z.infer<typeof YouTubeEmbedSchema>;

export type InvalidProperties = {
  acfFcLayout: LandingPageV2BlockTypes.InvalidProperties;
  blockName: string;
  error: ZodFormattedError<unknown>;
};

export type UnknownBlockType = {
  acfFcLayout: LandingPageV2BlockTypes.UnknownBlockType;
  block: unknown;
};

export type LandingPageBlockV2 =
  | AnchorBlock
  | ContentListCuratedBlock
  | ContentListSplitBlock
  | ContentListTabbedBlock
  | DownloadableAssetsBlock
  | FeatureListBlock
  | HubSpotFormEmbedBlock
  | ImageBlock
  | InvalidProperties
  | LeadGenerationFormBlock
  | SearchBlock
  | TabbedNavigationBlock
  | UnknownBlockType
  | WysiwygBlock
  | YouTubeEmbedBlock;

export const baseHeroSchema = z.object({
  background: backgroundSchema,
  maxWidth: maxWidthSchema.optional().default("medium"),
  wysiwyg: z.string(),
  includeCtas: z.boolean().nullable().optional(),
  primaryCta: linkSchema.nullable().optional(),
  secondaryCta: linkSchema.nullable().optional(),
  ctaAlignment: ctaAlignmentSchema,
});

export const HeroFullWidthSchema = baseHeroSchema.extend({
  appearance: z.literal("full_width"),
});
export type HeroFullWidth = z.infer<typeof HeroFullWidthSchema>;

export const HeroSplitWithImageSchema = baseHeroSchema.extend({
  appearance: z.literal("split_with_image"),
  image: imagePropertySchema,
  imagePosition: z.union([z.literal("left"), z.literal("right")]),
});
export type HeroSplitWithImage = z.infer<typeof HeroSplitWithImageSchema>;

export const HubSpotFormEmbedSchema = baseLandingPageBlockSchema.extend({
  acfFcLayout: z.literal(LandingPageV2BlockTypes.HubSpotForm),
  appearance: z.literal("full_width"),
  maxWidth: maxWidthSchema,
  wysiwyg: z.string().nullable().optional(),
  formId: z.string(),
});

export type HubSpotFormEmbedBlock = z.infer<typeof HubSpotFormEmbedSchema>;

export type LandingPageV2Hero = z.infer<
  typeof HeroFullWidthSchema | typeof HeroSplitWithImageSchema
>;

export interface LandingPageV2 {
  id: number;
  title: string;
  slug: string;
  type: string;
  hideFooter: boolean;
  hero: LandingPageV2Hero;
  blocks: Array<LandingPageBlockV2>;
  seo: NextSeoProps;
  structuredData: LandingPageStructDataProps;
}
